import { ChevronsUpDown, CircleCheck, LayoutGrid, UsersRound } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePermissions } from '@/hooks/use-auth';
import { useWorkspace } from '@/hooks/use-workspace';
import { cn } from '@/lib/utils';
import { findActiveModule } from '@/lib/navigation';
import { WORKSPACES, workspaceIncludes } from '@/lib/workspaces';

/**
 * Seletor de área de trabalho (só administradores): recorta a sidebar e o menu "Mais" pelos
 * módulos da função escolhida. `syncSelection` grava a área quando a rota leva a outra (o
 * componente montado no cabeçalho faz isso; cópias extras, como a do mobile, não precisam).
 */
export function WorkspaceSwitcher({
  className,
  syncSelection = false,
  onNavigateAway,
}: {
  className?: string;
  syncSelection?: boolean;
  /** Chamado ao sair para outra tela pelo menu (ex.: fechar a gaveta do mobile). */
  onNavigateAway?: () => void;
}) {
  const { enabled, selected, workspace, setWorkspace } = useWorkspace();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Só reage a mudanças de rota: na troca manual a área nova vale antes de a navegação concluir.
  const lastPath = useRef<string | null>(null);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    if (syncSelection && enabled && workspace.id !== selected) setWorkspace(workspace.id);
  }, [syncSelection, enabled, pathname, workspace.id, selected, setWorkspace]);

  if (!enabled) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn('max-w-56 justify-between gap-2', className)}
          aria-label={`Área de trabalho: ${workspace.label}`}
        >
          <LayoutGrid className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
          <span className="truncate">{workspace.label}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-70" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[min(22rem,calc(100vw-2rem))] p-2">
        <DropdownMenuLabel className="px-2 pt-1 pb-2 font-medium">
          Alterar função / área de trabalho
        </DropdownMenuLabel>
        {WORKSPACES.map((w) => {
          const active = w.id === workspace.id;
          return (
            <DropdownMenuItem
              key={w.id}
              onSelect={() => {
                setWorkspace(w.id);
                // Módulo aberto fora da área nova: volta ao início (senão a rota reabriria a área antiga).
                const module = findActiveModule(pathname);
                if (module && !workspaceIncludes(w, module)) void navigate('/dashboard');
              }}
              className={cn('gap-3 rounded-lg px-2 py-2 md:py-2', active && 'bg-primary-soft')}
            >
              <span
                className={cn(
                  'flex size-10 shrink-0 items-center justify-center rounded-lg',
                  active ? 'bg-primary/10' : 'bg-warning-soft',
                )}
              >
                <w.icon className="size-5 text-foreground" strokeWidth={1.75} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className={cn('block font-medium', active && 'text-primary')}>{w.label}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {w.description}
                </span>
              </span>
              {active ? (
                <CircleCheck className="size-5 fill-primary text-primary-foreground" aria-hidden />
              ) : null}
            </DropdownMenuItem>
          );
        })}
        {can('roles:read') ? (
          <>
            <DropdownMenuSeparator className="mx-0 my-2" />
            <DropdownMenuItem
              onSelect={() => {
                onNavigateAway?.();
                void navigate('/configuracoes/permissoes');
              }}
              className="gap-3 px-2 py-2.5 md:py-2.5"
            >
              <UsersRound className="size-5 text-foreground" strokeWidth={1.75} aria-hidden />
              Gerenciar perfis e permissões
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
